import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { fullworkAuthLogin } from '../pages/Login/LoginPresenter';
import { saveQrLoginSession } from '../utils/authSession';

const QR_CONTAINER_ID = 'wx_reg';
const AUTH_STATE_KEY = 'fuxiaozhu_wechat_auth_state';
const SCRIPT_ID = 'ww-login-script';

// Keep the existing callback path so the WeCom app's trusted redirect URI
// does not need to change. /wechatauth forwards to /login with the query.
const REDIRECT_URI = 'https://chat.henjiu.com/fuxiaozhu/wechatauth';
const SCRIPT_SRC = 'https://chat.henjiu.com/fuxiaozhu/js/sso/wwLogin-1.0.0.js';

const getRandomState = () => {
  if (window.crypto?.getRandomValues) {
    const array = new Uint32Array(4);
    window.crypto.getRandomValues(array);
    return Array.from(array, item => item.toString(16)).join('');
  }
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const loadWwLoginScript = () => new Promise((resolve, reject) => {
  if (window.WwLogin) {
    resolve();
    return;
  }

  const existingScript = document.getElementById(SCRIPT_ID);
  if (existingScript) {
    existingScript.addEventListener('load', resolve, { once: true });
    existingScript.addEventListener('error', reject, { once: true });
    return;
  }

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = SCRIPT_SRC;
  script.async = true;
  script.onload = resolve;
  script.onerror = reject;
  document.body.appendChild(script);
});

/**
 * Password login page's inline 富工作 QR block.
 *
 * The QR callback still lands on /wechatauth, whose route forwards to
 * /login with code/state, so this component can be mounted on one page.
 */
const FullworkQrLogin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const exchangedCodeRef = useRef(null);
  const initializedQrRef = useRef(false);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');

  const appid = 'ww2336ef003539f492';
  const agentid = '1000171';
  const href = 'https://chat.henjiu.com/fuxiaozhu/wechatauth';
  const host = 'https://mwork.fullgoal.com.cn:9981';

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state') || '';

    const clearCallbackQuery = () => {
      navigate(location.pathname, { replace: true });
    };

    const exchangeCode = async () => {
      if (exchangedCodeRef.current === code) return;
      exchangedCodeRef.current = code;
      initializedQrRef.current = false;
      setStatus('exchanging');
      setError('');

      const savedState = window.sessionStorage?.getItem(AUTH_STATE_KEY) || '';
      if (savedState && state && savedState !== state) {
        window.sessionStorage?.removeItem(AUTH_STATE_KEY);
        message.error('登录状态校验失败，请重新扫码登录');
        clearCallbackQuery();
        return;
      }

      try {
        const res = await fullworkAuthLogin({ code, state });
        if (res && res.status === 200) {
          const saveResult = saveQrLoginSession(res);
          if (saveResult.success) {
            window.sessionStorage?.removeItem(AUTH_STATE_KEY);
            message.success('扫码登录成功');
            navigate(saveResult.redirectPath, { replace: true });
            return;
          }
          throw new Error(saveResult.msg);
        }
        throw new Error(res?.msg || '扫码登录失败，请重新扫码');
      } catch (err) {
        message.error(err?.message || '扫码登录失败，请重新扫码');
        clearCallbackQuery();
      }
    };

    const renderQrCode = async () => {
      if (initializedQrRef.current) return;
      initializedQrRef.current = true;
      setStatus('loading');
      setError('');

      if (!appid || !agentid) {
        setStatus('error');
        setError('缺少富工作扫码登录配置，请配置 VITE_WW_APP_ID 和 VITE_WW_AGENT_ID');
        return;
      }

      try {
        await loadWwLoginScript();
        if (!window.WwLogin) {
          throw new Error('富工作扫码登录脚本加载失败');
        }

        const authState = getRandomState();
        window.sessionStorage?.setItem(AUTH_STATE_KEY, authState);
        const container = document.getElementById(QR_CONTAINER_ID);
        if (container) container.innerHTML = '';
        window.WwLogin({
          id: QR_CONTAINER_ID,
          appid,
          agentid,
          redirect_uri: encodeURIComponent(REDIRECT_URI),
          state: authState,
          href: encodeURIComponent(href),
          host,
        });
        setStatus('ready');
      } catch (err) {
        setStatus('error');
        setError(err?.message || '富工作扫码登录初始化失败，请稍后重试');
      }
    };

    if (code) {
      exchangeCode();
      return;
    }

    exchangedCodeRef.current = null;
    renderQrCode();
  }, [agentid, appid, href, host, location.pathname, location.search, navigate]);

  const handleRetry = () => {
    exchangedCodeRef.current = null;
    initializedQrRef.current = false;
    window.sessionStorage?.removeItem(AUTH_STATE_KEY);
    if (location.search) {
      navigate(location.pathname, { replace: true });
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="login-qr">
      <div className="login-form__divider"><span>或使用富工作扫码</span></div>

      {status === 'exchanging' ? (
        <div className="login-qr__status">
          <span className="login-qr__spinner" />
          正在登录，请稍候...
        </div>
      ) : (
        <>
          <div id={QR_CONTAINER_ID} className="login-qr__box" />
          {status === 'loading' && (
            <div className="login-qr__tip">正在加载扫码登录组件...</div>
          )}
          {status === 'ready' && (
            <div className="login-qr__tip">使用富工作扫码授权，成功后自动进入平台。</div>
          )}
          {status === 'error' && (
            <div className="login-qr__error">
              <div>{error}</div>
              <button type="button" onClick={handleRetry}>重新加载二维码</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FullworkQrLogin;
